import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { api, ApiError } from "../../shared/api/client";
import {
  Button,
  Drawer,
  Field,
  InlineAlert,
  Input,
  Select,
  Textarea,
  cn,
} from "../../shared/ui";
import type {
  BudgetCategory,
  EmployeeSummary,
  PageResponse,
  ProjectDetail,
} from "../../shared/types/api";
import { CATEGORY_LABELS, CATEGORY_ORDER, formatHours } from "./projectVocabulary";

const schema = z.object({
  projectKey: z
    .string()
    .min(1, "Enter a short key")
    .max(16, "Keep it under 16 characters")
    .regex(/^[A-Za-z0-9_-]+$/, "Letters, digits, hyphens and underscores only"),
  name: z.string().min(1, "Enter a name").max(200),
  description: z.string().max(4000).optional(),
  projectType: z.enum(["INTERNAL", "CUSTOMER"]),
  isPrivate: z.boolean(),
  methodology: z.enum(["SCRUM", "KANBAN", "HYBRID"]),
  plannedStartDate: z.string().min(1, "Choose a start date"),
  plannedEndDate: z.string().min(1, "Choose an end date"),
  sprintDurationDays: z.coerce.number().min(1).max(60),
  priority: z.enum(["CRITICAL", "HIGH", "MEDIUM", "LOW"]),
  riskLevel: z.enum(["CRITICAL", "HIGH", "MEDIUM", "LOW"]),
  ownerId: z.string().optional(),
  projectManagerId: z.string().optional(),
  customerName: z.string().max(200).optional(),
});

type FormValues = z.infer<typeof schema>;

const today = () => new Date().toISOString().slice(0, 10);

/**
 * Creates or edits a project, including its approved man-hour budget (§22, §24).
 *
 * <p>Budget is entered in hours per category and converted to minutes by the server. People plan
 * in hours; asking for minutes would be answered wrongly.
 *
 * <p>Editing the budget here does not touch an approved baseline. The gap between the commitment
 * and the plan is the variance the whole system exists to report — reconciling it silently would
 * erase the thing worth seeing.
 */
export function ProjectFormDrawer({
  project,
  open,
  onClose,
  onSaved,
}: {
  /** The project being edited, or undefined to create a new one. */
  project?: ProjectDetail;
  open: boolean;
  onClose: () => void;
  onSaved: (project: ProjectDetail) => void;
}) {
  const isEdit = Boolean(project);
  const [formError, setFormError] = useState<string | null>(null);
  const [budget, setBudget] = useState<Partial<Record<BudgetCategory, string>>>({});

  const employees = useQuery({
    queryKey: ["employees", "picker"],
    queryFn: () =>
      api.get<PageResponse<EmployeeSummary>>(
        "/api/v1/employees?active=true&size=200&sort=fullName,asc",
      ),
    enabled: open,
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  useEffect(() => {
    if (!open) {
      return;
    }
    reset({
      projectKey: project?.projectKey ?? "",
      name: project?.name ?? "",
      description: project?.description ?? "",
      projectType: project?.projectType ?? "INTERNAL",
      isPrivate: project?.isPrivate ?? false,
      methodology: project?.methodology ?? "SCRUM",
      plannedStartDate: project?.plannedStartDate ?? today(),
      plannedEndDate: project?.plannedEndDate ?? today(),
      sprintDurationDays: project?.sprintDurationDays ?? 14,
      priority: project?.priority ?? "MEDIUM",
      riskLevel: project?.riskLevel ?? "MEDIUM",
      ownerId: project?.ownerId ?? "",
      projectManagerId: project?.projectManagerId ?? "",
      customerName: project?.customerName ?? "",
    });
    setBudget(
      Object.fromEntries(
        CATEGORY_ORDER.map((category) => [
          category,
          project?.budgetHours?.[category] ? String(project.budgetHours[category]) : "",
        ]),
      ),
    );
    setFormError(null);
  }, [project, open, reset]);

  const budgetHours = Object.fromEntries(
    CATEGORY_ORDER.map((category) => [category, Number(budget[category] ?? "") || 0]).filter(
      ([, hours]) => (hours as number) > 0,
    ),
  ) as Partial<Record<BudgetCategory, number>>;

  const totalHours = Object.values(budgetHours).reduce<number>((sum, hours) => sum + hours, 0);

  const close = () => {
    setFormError(null);
    onClose();
  };

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null);
    try {
      const payload = {
        ...values,
        ownerId: values.ownerId || undefined,
        projectManagerId: values.projectManagerId || undefined,
      };

      let saved: ProjectDetail;
      if (isEdit && project) {
        saved = await api.patch<ProjectDetail>(`/api/v1/projects/${project.id}`, {
          ...payload,
          version: project.version,
        });
        // The budget has its own endpoint, so editing details cannot silently change the plan.
        saved = await api.put<ProjectDetail>(`/api/v1/projects/${project.id}/budget`, {
          budgetHours,
        });
      } else {
        saved = await api.post<ProjectDetail>("/api/v1/projects", { ...payload, budgetHours });
      }
      onSaved(saved);
      close();
    } catch (error) {
      setFormError(
        error instanceof ApiError && error.code === "DUPLICATE_RESOURCE"
          ? "A project with that key already exists."
          : error instanceof ApiError && error.isConcurrentModification
            ? "Someone else changed this project while you were editing. Close this panel and reopen it."
            : error instanceof ApiError
              ? error.message
              : "Could not save the project.",
      );
    }
  });

  return (
    <Drawer
      open={open}
      onClose={close}
      title={isEdit ? "Edit project" : "New project"}
      subtitle={isEdit ? project?.projectKey : "What is being committed to, and by when."}
      width="lg"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={close}>
            Cancel
          </Button>
          <Button onClick={() => void onSubmit()} loading={isSubmitting}>
            {isSubmitting ? "Saving…" : isEdit ? "Save changes" : "Create project"}
          </Button>
        </div>
      }
    >
      <form onSubmit={onSubmit} noValidate className="space-y-4">
        {formError && <InlineAlert tone="error">{formError}</InlineAlert>}

        {isEdit && project?.forecast.hasBaseline && (
          <InlineAlert tone="info">
            Changing the plan does not change the approved baseline. The difference becomes the
            variance shown on the overview — revising the commitment means approving a new baseline
            version.
          </InlineAlert>
        )}

        <div className="grid grid-cols-2 gap-4">
          <Field label="Key" required error={errors.projectKey?.message}>
            <Input
              autoFocus={!isEdit}
              disabled={isEdit}
              placeholder="MB"
              {...register("projectKey")}
            />
          </Field>
          <Field label="Type" required error={errors.projectType?.message}>
            <Select {...register("projectType")}>
              <option value="INTERNAL">Internal</option>
              <option value="CUSTOMER">Customer</option>
            </Select>
          </Field>
        </div>

        {/* Its own setting rather than a reading of the type: internal and private are different
            questions. Most work that is not for a customer is nobody's secret, and work delivered
            for a customer may be the most confidential thing in the building. */}
        <Field label="Who can see it" error={errors.isPrivate?.message}>
          <label className="flex items-start gap-2.5 rounded-lg border border-ink-200 p-3">
            <input
              type="checkbox"
              className="mt-0.5 h-4 w-4 rounded border-ink-300"
              {...register("isPrivate")}
            />
            <span className="text-sm">
              <span className="font-medium text-ink-900">Private to its members</span>
              <span className="mt-0.5 block text-xs text-ink-500">
                Nobody outside the project sees it, or anything in it — not in lists, not in
                search, not in reports. Add its members first: a private project with none is
                visible only to a system administrator.
              </span>
            </span>
          </label>
        </Field>

        <Field label="Name" required error={errors.name?.message}>
          <Input placeholder="Mobile Banking" {...register("name")} />
        </Field>

        <Field label="Description" error={errors.description?.message} hint="Optional">
          <Textarea rows={2} {...register("description")} />
        </Field>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Planned start" required error={errors.plannedStartDate?.message}>
            <Input type="date" {...register("plannedStartDate")} />
          </Field>
          <Field label="Planned finish" required error={errors.plannedEndDate?.message}>
            <Input type="date" {...register("plannedEndDate")} />
          </Field>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <Field label="Methodology" required>
            <Select {...register("methodology")}>
              <option value="SCRUM">Scrum</option>
              <option value="KANBAN">Kanban</option>
              <option value="HYBRID">Hybrid</option>
            </Select>
          </Field>
          <Field label="Sprint days" required error={errors.sprintDurationDays?.message}>
            <Input type="number" min={1} max={60} {...register("sprintDurationDays")} />
          </Field>
          <Field label="Priority" required>
            <Select {...register("priority")}>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <Field label="Project manager" hint="Optional">
            <Select {...register("projectManagerId")} disabled={employees.isPending}>
              <option value="">Not assigned</option>
              {(employees.data?.content ?? []).map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.fullName}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Risk level" required>
            <Select {...register("riskLevel")}>
              <option value="CRITICAL">Critical</option>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </Select>
          </Field>
        </div>

        <section className="border-t border-ink-100 pt-4">
          <h3 className="text-sm font-semibold text-ink-900">Approved man-hours</h3>
          <p className="mb-3 mt-0.5 text-xs text-ink-400">
            In hours, by discipline (§24). Leave a category blank if it is not budgeted.
          </p>

          <div className="overflow-hidden rounded-lg border border-ink-200">
            {CATEGORY_ORDER.map((category) => (
              <div
                key={category}
                className={cn(
                  "flex items-center justify-between gap-3 border-b border-ink-100 px-3 py-2 last:border-0",
                  !budget[category] && "bg-ink-50/60",
                )}
              >
                <span className="text-sm text-ink-700">{CATEGORY_LABELS[category]}</span>
                <div className="w-28">
                  <Input
                    type="number"
                    min={0}
                    step={10}
                    placeholder="—"
                    className="text-right"
                    value={budget[category] ?? ""}
                    onChange={(event) =>
                      setBudget((current) => ({ ...current, [category]: event.target.value }))
                    }
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-2 flex justify-between text-sm">
            <span className="text-ink-500">Total approved</span>
            <span className="tnum font-semibold text-ink-900">{formatHours(totalHours)}</span>
          </div>
        </section>
      </form>
    </Drawer>
  );
}
